/**
 * Change dialogs of one connection: environment address and settings, credentials, client
 * certificates, revocation, destroying a stored value and the connection record itself. Each
 * dialog shows the environment being changed, needs a reason and an explicit confirmation, and
 * reports whether the change was applied or is waiting for a second person.
 */

import { Ban, FileKey2, FileUp, Save, Trash2 } from 'lucide-react';
import { useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api, isApiError } from '../../../api';
import type {
  AuthMethod,
  CertificateVersion,
  ConnectionDetail,
  ConnectionStatus,
  CredentialVersion,
  EnvironmentDetail,
  EppSettings,
  ImportPkcs12Body,
  PresentedCertificate,
  IntegrationEnvironment,
  Pkcs12ImportResult,
  ProposeCertificateBody,
  ProposeCredentialBody,
  ProposeEnvironmentBody,
  RestSettings,
  RevokeBody,
  TrafficClass,
  UpdateConnectionBody,
  WriteResult,
} from '../../../api/areas/integrations';
import { Callout, Field } from '../../../components/blocks';
import { KeyValue } from '../../../components/KeyValue';
import { CopyText } from '../../../components/States';
import { useWrite } from '../../../components/Write';
import { formatDateTime } from '../../../lib/format';
import { newIdempotencyKey } from '../../../lib/ids';
import { useApi } from '../../../lib/useApi';
import {
  ChangeDialog,
  ConfirmCheck,
  emptySecret,
  EnvironmentBanner,
  IntegrationError,
  NumberField,
  PresetFill,
  ReasonField,
  SecretField,
  secretDraftValid,
  secretInput,
  SelectField,
  SwitchRow,
  tx,
  useIntegrationContext,
  WriteOutcome,
  type SecretDraft,
} from '../shared';
import { isWebnic, webnicPresets as webnicAddressPresets, webnicRest } from '../presets';
import { WebnicAllowListNote } from './webnic';

const EPP_DEFAULTS: EppSettings = {
  host: '',
  port: 700,
  tls: true,
  server_name: null,
  ca_pem: null,
  connect_timeout_ms: 10_000,
  command_timeout_ms: 30_000,
  acquire_timeout_ms: 5_000,
  failure_threshold: 5,
  open_for_ms: 30_000,
  max_commands_per_session: null,
  max_session_idle_ms: null,
  max_session_lifetime_ms: null,
  classes: {},
};

const REST_DEFAULTS: RestSettings = {
  base_url: '',
  auth_method: 'HTTP_BASIC',
  auth_settings: {},
  account_id: null,
  connect_timeout_ms: 10_000,
  request_timeout_ms: 30_000,
};

/** The auth parameters the chosen method uses, trimmed, without empty values: the server
 * rejects keys a method does not use and empty addresses or header names. */
function authSettingsFor(method: AuthMethod, settings: RestSettings['auth_settings']): RestSettings['auth_settings'] {
  const keys: (keyof RestSettings['auth_settings'])[] = method === 'API_KEY_HEADER' ? ['api_key_header'] : method === 'OAUTH2_CLIENT_CREDENTIALS' ? ['token_url', 'scope'] : [];
  const out: RestSettings['auth_settings'] = {};
  for (const k of keys) {
    const v = settings[k]?.trim();
    if (v) out[k] = v;
  }
  return out;
}

function Done({ result, env, onClose, extra }: { result: WriteResult<unknown>; env?: IntegrationEnvironment | null; onClose: () => void; extra?: ReactNode }) {
  const { t } = useTranslation('integrations');
  return (
    <div className="space-y-4">
      <WriteOutcome result={result.result} approval={result.approval} warnings={result.warnings} env={env} />
      {extra}
      <DialogFooter>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogFooter>
    </div>
  );
}

function Footer({ onCancel, onSubmit, disabled, busy, env, label }: { onCancel: () => void; onSubmit: () => void; disabled: boolean; busy: boolean; env?: IntegrationEnvironment | null; label?: string }) {
  const { t } = useTranslation('integrations');
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <Button type="button" onClick={onSubmit} disabled={disabled || busy} variant={env === 'PRODUCTION' ? 'destructive' : 'default'}>
        <Save /> {busy ? t('common.saving') : (label ?? (env === 'PRODUCTION' ? t('form.submitForApproval') : t('form.saveNow')))}
      </Button>
    </DialogFooter>
  );
}

/** EPP settings as the API accepts them (without the read-only CA description). */
export function eppForWrite(epp: EppSettings): EppSettings {
  const { ca_certificates: _described, ...rest } = epp;
  return rest;
}

const MAX_PEM_FILE = 64 * 1024;

/**
 * A certificate or key file as PEM text: PEM files are read as they are; binary (DER, e.g. a
 * `.cer` / `.der` from Windows) is wrapped as `label`.
 */
export async function pemFromFile(file: File, label: 'CERTIFICATE' | 'PRIVATE KEY'): Promise<string> {
  if (file.size > MAX_PEM_FILE) throw new Error('too large');
  const bytes = new Uint8Array(await file.arrayBuffer());
  const text = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  if (text.includes('-----BEGIN ')) return text.trim() + '\n';
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  bytes.fill(0);
  const b64 = btoa(binary);
  return `-----BEGIN ${label}-----\n${(b64.match(/.{1,64}/g) ?? []).join('\n')}\n-----END ${label}-----\n`;
}

/** Whether a PEM private key is password-protected (PKCS#8 or traditional OpenSSL). */
export function isEncryptedKey(pem: string): boolean {
  return pem.includes('-----BEGIN ENCRYPTED PRIVATE KEY-----') || pem.includes('Proc-Type: 4,ENCRYPTED');
}

/** A small "load from file" button for PEM fields. */
export function PemFileButton({ label, accept, kind, onLoad }: { label: string; accept: string; kind: 'CERTIFICATE' | 'PRIVATE KEY'; onLoad: (pem: string) => void }) {
  const { t } = useTranslation('integrations');
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState(false);
  return (
    <span className="inline-flex items-center gap-2">
      <input
        ref={input}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          setError(false);
          pemFromFile(f, kind).then(onLoad, () => setError(true));
        }}
      />
      <Button type="button" size="xs" variant="outline" onClick={() => input.current?.click()}>
        <FileUp /> {label}
      </Button>
      {error ? <span className="text-xs text-critical">{t('certFiles.unreadable')}</span> : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Environment (address, connection settings)

export type EnvironmentSection = 'endpoint' | 'settings';

export function EnvironmentDialog({
  open,
  onOpenChange,
  connection,
  env,
  detail,
  section,
  onDone,
  startFromPreset = false,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  connection: ConnectionDetail;
  env: IntegrationEnvironment;
  detail: EnvironmentDetail;
  section: EnvironmentSection;
  onDone: () => void;
  /** Open with the provider's published address filled in (VNNIC EPP, WebNIC). */
  startFromPreset?: boolean;
}) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog
      open={open}
      onOpenChange={onOpenChange}
      title={section === 'endpoint' ? t('envDialog.titleEndpoint', { name: connection.name }) : t('envDialog.titleSettings', { name: connection.name })}
      description={t('envDialog.description')}
      wide
    >
      <EnvironmentForm connection={connection} env={env} detail={detail} section={section} startFromPreset={startFromPreset} onClose={() => onOpenChange(false)} onDone={onDone} />
    </ChangeDialog>
  );
}

function EnvironmentForm({
  connection,
  env,
  detail,
  section,
  startFromPreset,
  onClose,
  onDone,
}: {
  connection: ConnectionDetail;
  env: IntegrationEnvironment;
  detail: EnvironmentDetail;
  section: EnvironmentSection;
  startFromPreset: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  // VNNIC / WebNIC publish an OT&E and a production address; this server's is the starting point
  // and the other one can still be filled in (the "fill in the OT&E / production address" helper).
  const eppPresets = connection.adapter === 'vnnic-epp' ? ctx.epp_presets : [];
  const preset = eppPresets.find((p) => p.environment === env);
  const webnic = isWebnic(connection);
  const base = detail.active;
  const webnicPreset = webnic ? webnicRest(env, base?.rest) : null;
  const webnicPresets = webnic ? webnicAddressPresets(base?.rest) : [];
  const [enabled, setEnabled] = useState(base?.enabled ?? true);
  const [epp, setEpp] = useState<EppSettings>(() => (startFromPreset && preset ? { ...preset.settings } : (base?.epp ?? (preset ? preset.settings : { ...EPP_DEFAULTS }))));
  const [rest, setRest] = useState<RestSettings>(() => (startFromPreset && webnicPreset ? webnicPreset : (base?.rest ?? webnicPreset ?? { ...REST_DEFAULTS })));
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: ProposeEnvironmentBody, opts) => api.proposeEnvironmentVersion(connection.id, body, opts));
  const isEpp = connection.protocol === 'EPP';
  const production = env === 'PRODUCTION';
  // An adapter that declares its methods accepts only those; without an adapter, every generic method.
  const declared = ctx.rest_adapters.find((a) => a.key === connection.adapter)?.auth_methods;
  const authMethods = declared ?? ctx.auth_methods.filter((m) => m !== 'ADAPTER_DEFINED');
  const setE = (patch: Partial<EppSettings>) => setEpp((s) => ({ ...s, ...patch }));
  const setR = (patch: Partial<RestSettings>) => setRest((s) => ({ ...s, ...patch }));
  const authOk =
    isEpp ||
    (rest.auth_method === 'API_KEY_HEADER' ? !!rest.auth_settings.api_key_header?.trim() : rest.auth_method === 'OAUTH2_CLIENT_CREDENTIALS' ? !!rest.auth_settings.token_url?.trim() : true);
  const endpointOk = isEpp ? epp.host.trim().length > 0 && epp.port > 0 : rest.base_url.trim().length > 0 && authOk;
  const httpInProduction = !isEpp && production && rest.base_url.trim().toLowerCase().startsWith('http://');

  const submit = async () => {
    const res = await write.run({
      expected_generation: detail.generation,
      enabled,
      epp: isEpp ? { ...eppForWrite(epp), host: epp.host.trim(), tls: production ? true : epp.tls, server_name: epp.server_name?.trim() || null, ca_pem: epp.ca_pem?.trim() || null } : null,
      rest: isEpp ? null : { ...rest, base_url: rest.base_url.trim(), auth_settings: authSettingsFor(rest.auth_method, rest.auth_settings), account_id: rest.account_id?.trim() || null },
      reason: reason.trim(),
    });
    if (res) onDone();
  };
  if (write.state.phase === 'done') return <Done result={write.state.result} env={env} onClose={onClose} />;

  const sessions = Object.values(epp.classes).reduce((n, c) => n + (c?.sessions ?? 0), 0);

  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      {!detail.exists ? <Callout tone="info">{t('envDialog.firstTime')}</Callout> : null}
      <SwitchRow label={t('envDialog.enabled')} hint={<span className="block text-xs text-muted-foreground">{t('envDialog.enabledHint')}</span>} checked={enabled} onChange={setEnabled} />

      {section === 'endpoint' && isEpp ? (
        <>
          <PresetFill
            env={env}
            presets={eppPresets.map((p) => ({ environment: p.environment, value: p.settings }))}
            describe={(s) => `${s.host}:${s.port}`}
            matches={(s) => epp.host === s.host && epp.port === s.port}
            onUse={(s) => setEpp({ ...s })}
          />
          <div className="grid items-start gap-3 sm:grid-cols-3">
            <Field label={t('fields.epp_host')} className="sm:col-span-2" hint={t('envDialog.hostHint')}>
              <Input value={epp.host} onChange={(e) => setE({ host: e.target.value })} autoComplete="off" spellCheck={false} className="font-mono" />
            </Field>
            <NumberField label={t('fields.epp_port')} value={epp.port} onChange={(v) => setE({ port: v ?? 0 })} min={1} max={65535} raw />
          </div>
          <SwitchRow
            label={t('fields.epp_tls')}
            hint={<span className="block text-xs text-muted-foreground">{production ? t('envDialog.tlsRequired') : t('envDialog.tlsHint')}</span>}
            checked={production ? true : epp.tls}
            disabled={production}
            onChange={(v) => setE({ tls: v })}
          />
          <details className="rounded-lg border p-3 text-sm">
            <summary className="cursor-pointer font-medium">{t('envDialog.advancedTls')}</summary>
            <div className="mt-3 grid gap-3">
              <Field label={t('fields.epp_server_name')} hint={t('envDialog.serverNameHint')}>
                <Input value={epp.server_name ?? ''} onChange={(e) => setE({ server_name: e.target.value || null })} autoComplete="off" spellCheck={false} className="font-mono" />
              </Field>
              <Field label={t('fields.epp_ca_pem')} hint={t('envDialog.caHint')}>
                <Textarea value={epp.ca_pem ?? ''} onChange={(e) => setE({ ca_pem: e.target.value || null })} rows={4} spellCheck={false} className="font-mono text-xs" />
              </Field>
            </div>
          </details>
        </>
      ) : null}

      {section === 'endpoint' && !isEpp ? (
        <>
          <PresetFill
            env={env}
            presets={webnicPresets}
            describe={(r) => r.base_url}
            matches={(r) => rest.base_url.trim() === r.base_url && rest.auth_method === r.auth_method}
            onUse={(r) => setRest(r)}
          />
          <Field label={t('fields.rest_base_url')} hint={httpInProduction ? <span className="text-critical">{t('envDialog.httpsRequired')}</span> : t('envDialog.baseUrlHint')}>
            <Input value={rest.base_url} onChange={(e) => setR({ base_url: e.target.value })} autoComplete="off" spellCheck={false} className="font-mono" placeholder="https://" />
          </Field>
          <div className="grid items-start gap-3 sm:grid-cols-2">
            <SelectField<AuthMethod>
              label={t('fields.rest_auth_method')}
              value={rest.auth_method}
              onChange={(v) => setR({ auth_method: v, auth_settings: {} })}
              options={authMethods.map((m) => ({ value: m, label: webnic && m === 'ADAPTER_DEFINED' ? t('webnic.authMethod') : t(`authMethod.${m}`) }))}
            />
            <Field label={t('fields.rest_account_id')} hint={t('envDialog.accountHint')}>
              <Input value={rest.account_id ?? ''} onChange={(e) => setR({ account_id: e.target.value || null })} autoComplete="off" />
            </Field>
            {rest.auth_method === 'API_KEY_HEADER' ? (
              <Field label={t('fields.rest_auth_settings_api_key_header')} hint={t('envDialog.headerHint')}>
                <Input value={rest.auth_settings.api_key_header ?? ''} onChange={(e) => setR({ auth_settings: { ...rest.auth_settings, api_key_header: e.target.value } })} className="font-mono" />
              </Field>
            ) : null}
            {rest.auth_method === 'OAUTH2_CLIENT_CREDENTIALS' ? (
              <>
                <Field label={t('fields.rest_auth_settings_token_url')}>
                  <Input value={rest.auth_settings.token_url ?? ''} onChange={(e) => setR({ auth_settings: { ...rest.auth_settings, token_url: e.target.value } })} className="font-mono" placeholder="https://" />
                </Field>
                <Field label={t('fields.rest_auth_settings_scope')} hint={t('form.optional')}>
                  <Input value={rest.auth_settings.scope ?? ''} onChange={(e) => setR({ auth_settings: { ...rest.auth_settings, scope: e.target.value || undefined } })} />
                </Field>
              </>
            ) : null}
          </div>
          {webnic && rest.auth_method === 'ADAPTER_DEFINED' ? <p className="text-xs text-muted-foreground">{t('webnic.authHint')}</p> : null}
          <p className="text-xs text-muted-foreground">{t('envDialog.credentialsElsewhere')}</p>
          {webnic ? <WebnicAllowListNote /> : null}
          {connection.adapter_status !== 'AVAILABLE' ? <Callout tone="warn" title={t('adapterNotice.title')}>{t('adapterNotice.body')}</Callout> : null}
        </>
      ) : null}

      {section === 'settings' && isEpp ? (
        <>
          <div className="grid items-start gap-3 sm:grid-cols-3">
            <NumberField label={t('fields.epp_connect_timeout_ms')} value={epp.connect_timeout_ms} onChange={(v) => setE({ connect_timeout_ms: v ?? 0 })} min={1000} max={60000} unit="ms" />
            <NumberField label={t('fields.epp_command_timeout_ms')} value={epp.command_timeout_ms} onChange={(v) => setE({ command_timeout_ms: v ?? 0 })} min={1000} max={300000} unit="ms" />
            <NumberField label={t('fields.epp_acquire_timeout_ms')} value={epp.acquire_timeout_ms} onChange={(v) => setE({ acquire_timeout_ms: v ?? 0 })} min={100} max={60000} unit="ms" />
            <NumberField label={t('fields.epp_failure_threshold')} value={epp.failure_threshold} onChange={(v) => setE({ failure_threshold: v ?? 0 })} min={1} max={100} />
            <NumberField label={t('fields.epp_open_for_ms')} value={epp.open_for_ms} onChange={(v) => setE({ open_for_ms: v ?? 0 })} min={1000} max={600000} unit="ms" />
          </div>
          <div className="grid items-start gap-3 sm:grid-cols-3">
            <NumberField label={t('fields.epp_max_commands_per_session')} value={epp.max_commands_per_session} onChange={(v) => setE({ max_commands_per_session: v })} optional />
            <NumberField label={t('fields.epp_max_session_idle_ms')} value={epp.max_session_idle_ms} onChange={(v) => setE({ max_session_idle_ms: v })} unit="ms" optional />
            <NumberField label={t('fields.epp_max_session_lifetime_ms')} value={epp.max_session_lifetime_ms} onChange={(v) => setE({ max_session_lifetime_ms: v })} unit="ms" optional />
          </div>
          <ClassesEditor classes={epp.classes} onChange={(classes) => setE({ classes })} />
          <p className="text-xs text-muted-foreground">{t('envDialog.totalSessions', { count: sessions })}</p>
        </>
      ) : null}

      {section === 'settings' && !isEpp ? (
        <div className="grid items-start gap-3 sm:grid-cols-2">
          <NumberField label={t('fields.rest_connect_timeout_ms')} value={rest.connect_timeout_ms} onChange={(v) => setR({ connect_timeout_ms: v ?? 0 })} min={1000} max={60000} unit="ms" />
          <NumberField label={t('fields.rest_request_timeout_ms')} value={rest.request_timeout_ms} onChange={(v) => setR({ request_timeout_ms: v ?? 0 })} min={1000} max={300000} unit="ms" />
        </div>
      ) : null}

      {section === 'settings' ? <p className="text-xs text-muted-foreground">{t('envDialog.otherFieldsKept')}</p> : <p className="text-xs text-muted-foreground">{t('envDialog.settingsKept')}</p>}
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <Footer onCancel={onClose} onSubmit={() => void submit()} disabled={!endpointOk || httpInProduction || !reason.trim() || !confirmed} busy={write.busy} env={env} />
    </div>
  );
}

function ClassesEditor({ classes, onChange }: { classes: EppSettings['classes']; onChange: (c: EppSettings['classes']) => void }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const baseId = useId();
  const set = (cls: TrafficClass, patch: Partial<{ sessions: number; requests_per_second: number; burst: number }> | null) => {
    const next = { ...classes };
    if (patch === null) delete next[cls];
    else next[cls] = { sessions: 1, requests_per_second: 5, burst: 5, ...next[cls], ...patch };
    onChange(next);
  };
  return (
    <fieldset className="grid gap-2 rounded-lg border p-3">
      <legend className="px-1 text-sm font-medium">{t('settingsTab.pools')}</legend>
      <p className="text-xs text-muted-foreground">{t('settingsTab.poolsHint')}</p>
      {ctx.traffic_classes.map((cls) => {
        const c = classes[cls];
        return (
          <div key={cls} className="grid items-end gap-2 rounded-md border p-2 sm:grid-cols-[minmax(10rem,1fr)_repeat(3,minmax(0,7rem))]">
            <div className="flex items-center gap-2 text-sm">
              <Checkbox id={`${baseId}-${cls}`} checked={!!c} onCheckedChange={(v) => set(cls, v === true ? {} : null)} />
              <label htmlFor={`${baseId}-${cls}`}>
                <span className="block font-medium">{t(`trafficClass.${cls}`)}</span>
                <span className="block text-xs text-muted-foreground">{t(`trafficClassHint.${cls}`)}</span>
              </label>
            </div>
            {c ? (
              <>
                <NumberField label={t('fields.class_sessions')} value={c.sessions} onChange={(v) => set(cls, { sessions: v ?? 0 })} min={0} max={100} />
                <NumberField label={t('fields.class_requests_per_second')} value={c.requests_per_second} onChange={(v) => set(cls, { requests_per_second: v ?? 0 })} min={1} max={1000} />
                <NumberField label={t('fields.class_burst')} value={c.burst} onChange={(v) => set(cls, { burst: v ?? 0 })} min={1} max={1000} />
              </>
            ) : null}
          </div>
        );
      })}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Credentials

function credentialLabels(protocol: 'EPP' | 'REST', auth: AuthMethod | undefined, adapter?: string): { clientId: string | null; secret: string } {
  if (protocol === 'EPP') return { clientId: 'credDialog.eppClientId', secret: 'credDialog.eppPassword' };
  if (isWebnic({ protocol, adapter: adapter ?? '' })) return { clientId: 'webnic.username', secret: 'webnic.secret' };
  switch (auth) {
    case 'BEARER_TOKEN':
      return { clientId: null, secret: 'credDialog.token' };
    case 'API_KEY_HEADER':
      return { clientId: null, secret: 'credDialog.apiKey' };
    case 'OAUTH2_CLIENT_CREDENTIALS':
    case 'ADAPTER_DEFINED':
      return { clientId: 'credDialog.oauthClientId', secret: 'credDialog.clientSecret' };
    default:
      return { clientId: 'credDialog.username', secret: 'credDialog.password' };
  }
}

type LooseT = (key: string, options?: Record<string, unknown>) => string;

export function CredentialDialog({ open, onOpenChange, connection, env, detail, onDone }: { open: boolean; onOpenChange: (v: boolean) => void; connection: ConnectionDetail; env: IntegrationEnvironment; detail: EnvironmentDetail; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog open={open} onOpenChange={onOpenChange} title={t('credDialog.title', { name: connection.name })} description={t('credDialog.description')} wide>
      <CredentialForm connection={connection} env={env} detail={detail} onClose={() => onOpenChange(false)} onDone={onDone} />
    </ChangeDialog>
  );
}

function CredentialForm({ connection, env, detail, onClose, onDone }: { connection: ConnectionDetail; env: IntegrationEnvironment; detail: EnvironmentDetail; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const lt = t as unknown as LooseT;
  const active = detail.credentials.find((c) => c.status === 'ACTIVE');
  const canKeep = !!active?.secret.configured && !active.secret.destroyed;
  const labels = credentialLabels(connection.protocol, detail.active?.rest?.auth_method, connection.adapter);
  const webnic = isWebnic(connection);
  const [clientId, setClientId] = useState(active?.client_id ?? '');
  const [secret, setSecret] = useState<SecretDraft>(() => emptySecret(canKeep));
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: ProposeCredentialBody, opts) => api.proposeCredential(connection.id, body, opts));
  const isEpp = connection.protocol === 'EPP';
  const clientOk = !isEpp || (clientId.trim().length >= 3 && clientId.trim().length <= 16);
  const passwordOk = !isEpp || secret.mode !== 'VALUE' || (secret.value.length >= 6 && secret.value.length <= 16);
  const changed = secret.mode !== 'KEEP' || clientId.trim() !== (active?.client_id ?? '');
  const valid = clientOk && passwordOk && secretDraftValid(secret) && changed && reason.trim() && confirmed;

  const submit = async () => {
    const res = await write.run({ expected_active_version: active?.version ?? null, client_id: labels.clientId ? clientId.trim() : '', secret: secretInput(secret), reason: reason.trim() });
    if (res) {
      setSecret(emptySecret(false));
      onDone();
    }
  };
  if (write.state.phase === 'done') return <Done result={write.state.result} env={env} onClose={onClose} />;
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      {active ? (
        <p className="text-sm text-muted-foreground">{t('credDialog.current', { version: active.version })}</p>
      ) : (
        <p className="text-sm text-muted-foreground">{t('credDialog.none')}</p>
      )}
      {labels.clientId ? (
        <Field label={lt(labels.clientId)} hint={isEpp ? (clientId && !clientOk ? <span className="text-critical">{t('credDialog.eppClientIdInvalid')}</span> : t('credDialog.eppClientIdHint')) : undefined}>
          <Input value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" spellCheck={false} className="font-mono" maxLength={isEpp ? 16 : 200} />
        </Field>
      ) : null}
      <SecretField
        label={lt(labels.secret)}
        draft={secret}
        onChange={setSecret}
        canKeep={canKeep}
        keepLabel={webnic ? t('webnic.keepSecret') : t('credDialog.keep')}
        env={env}
        hint={isEpp ? (secret.mode === 'VALUE' && secret.value && !passwordOk ? <span className="text-critical">{t('credDialog.eppPasswordInvalid')}</span> : t('credDialog.eppPasswordHint')) : webnic ? t('webnic.credentialHint') : t('credDialog.restHint')}
      />
      {webnic ? <WebnicAllowListNote compact /> : null}
      <p className="text-xs text-muted-foreground">{env === 'PRODUCTION' ? t('credDialog.rotateProduction') : t('credDialog.rotateTest')}</p>
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <Footer onCancel={onClose} onSubmit={() => void submit()} disabled={!valid} busy={write.busy} env={env} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Certificates

export function CertificateDialog({ open, onOpenChange, connection, env, detail, onDone }: { open: boolean; onOpenChange: (v: boolean) => void; connection: ConnectionDetail; env: IntegrationEnvironment; detail: EnvironmentDetail; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog open={open} onOpenChange={onOpenChange} title={t('certDialog.title', { name: connection.name })} description={t('certDialog.description')} wide>
      <CertificateForm connection={connection} env={env} detail={detail} onClose={() => onOpenChange(false)} onDone={onDone} />
    </ChangeDialog>
  );
}

function CertificateForm({ connection, env, detail, onClose, onDone }: { connection: ConnectionDetail; env: IntegrationEnvironment; detail: EnvironmentDetail; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const active = (detail.certificates ?? []).find((c) => c.status === 'ACTIVE');
  const canKeep = !!active?.private_key.configured && !active.private_key.destroyed;
  const [pem, setPem] = useState('');
  const [key, setKey] = useState<SecretDraft>(() => emptySecret(canKeep));
  const [keyPassword, setKeyPassword] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: ProposeCertificateBody, opts) => api.proposeCertificate(connection.id, body, opts));
  const pemOk = pem.includes('-----BEGIN CERTIFICATE-----') && pem.includes('-----END CERTIFICATE-----');
  const encryptedKey = key.mode === 'VALUE' && isEncryptedKey(key.value);
  const valid = pemOk && secretDraftValid(key) && (!encryptedKey || keyPassword.length > 0) && reason.trim() && confirmed;
  const submit = async () => {
    const res = await write.run({
      expected_active_version: active?.version ?? null,
      certificate_pem: pem.trim(),
      private_key: secretInput(key),
      private_key_password: encryptedKey ? keyPassword : null,
      reason: reason.trim(),
    });
    setKeyPassword('');
    if (res) {
      setKey(emptySecret(false));
      onDone();
    }
  };
  if (write.state.phase === 'done') return <Done result={write.state.result} env={env} onClose={onClose} />;
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      <Callout tone="info">{t('certFiles.threeFiles')}</Callout>
      <Field label={t('certDialog.pem')} hint={pem && !pemOk ? <span className="text-critical">{t('certDialog.pemInvalid')}</span> : t('certDialog.pemHint')}>
        <div className="space-y-2">
          <PemFileButton label={t('certFiles.loadCertificate')} accept=".crt,.cer,.pem,.der" kind="CERTIFICATE" onLoad={setPem} />
          <Textarea value={pem} onChange={(e) => setPem(e.target.value)} rows={6} spellCheck={false} className="font-mono text-xs" maxLength={65536} />
        </div>
      </Field>
      <div className="space-y-2">
        <PemFileButton label={t('certFiles.loadKey')} accept=".key,.pem,.der" kind="PRIVATE KEY" onLoad={(v) => setKey({ ...key, mode: 'VALUE', value: v })} />
        <SecretField label={t('certDialog.key')} draft={key} onChange={setKey} canKeep={canKeep} keepLabel={t('certDialog.keepKey')} env={env} multiline hint={t('certDialog.keyHint')} />
      </div>
      {encryptedKey ? (
        <Field label={t('certFiles.keyPassword')} hint={t('certFiles.keyPasswordHint')}>
          <Input type="password" value={keyPassword} onChange={(e) => setKeyPassword(e.target.value)} autoComplete="off" />
        </Field>
      ) : null}
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <Footer onCancel={onClose} onSubmit={() => void submit()} disabled={!valid} busy={write.busy} env={env} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PKCS#12 (.p12 / .pfx) import: key, certificate and chain in one file

const PKCS12_LIMIT = 64 * 1024;

/** The file as standard base64; the byte copy is overwritten once encoded. */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  bytes.fill(0);
  return btoa(binary);
}

function formatBytes(n: number): string {
  return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`;
}

export function Pkcs12Dialog({ open, onOpenChange, connection, env, detail, onDone }: { open: boolean; onOpenChange: (v: boolean) => void; connection: ConnectionDetail; env: IntegrationEnvironment; detail: EnvironmentDetail; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog open={open} onOpenChange={onOpenChange} title={t('pkcs12.title', { name: connection.name })} description={t('pkcs12.description')} wide>
      <Pkcs12Form connection={connection} env={env} detail={detail} onClose={() => onOpenChange(false)} onDone={onDone} />
    </ChangeDialog>
  );
}

type ImportState = { phase: 'idle' } | { phase: 'busy' } | { phase: 'done'; result: Pkcs12ImportResult } | { phase: 'error'; error: unknown };

function Pkcs12Form({ connection, env, detail, onClose, onDone }: { connection: ConnectionDetail; env: IntegrationEnvironment; detail: EnvironmentDetail; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const fileId = useId();
  const active = (detail.certificates ?? []).find((c) => c.status === 'ACTIVE');
  const [file, setFile] = useState<File | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [state, setState] = useState<ImportState>({ phase: 'idle' });
  // One Idempotency-Key per attempt, reused only to repeat an attempt whose answer never arrived
  // (same file and reason). The passphrase is not part of the comparison, so it is never kept.
  const attempt = useRef<{ key: string; file: File; reason: string; unanswered: boolean } | null>(null);
  const tooLarge = !!file && file.size > PKCS12_LIMIT;
  const valid = !!file && !tooLarge && file.size > 0 && passphrase.length > 0 && reason.trim().length > 0 && confirmed;

  const submit = async () => {
    if (!file) return;
    const why = reason.trim();
    const prev = attempt.current;
    const key = prev && prev.unanswered && prev.file === file && prev.reason === why ? prev.key : newIdempotencyKey();
    attempt.current = { key, file, reason: why, unanswered: true };
    setState({ phase: 'busy' });
    // The passphrase leaves component state as soon as it is sent, whatever the outcome.
    const secret = passphrase;
    setPassphrase('');
    try {
      const body: ImportPkcs12Body = { expected_active_version: active?.version ?? null, file_base64: await fileToBase64(file), passphrase: secret, reason: why };
      const result = await api.importCertificatePkcs12(connection.id, body, { idempotencyKey: key });
      attempt.current = null;
      setFile(null);
      setState({ phase: 'done', result });
      onDone();
    } catch (error) {
      if (attempt.current && isApiError(error)) attempt.current.unanswered = false;
      setState({ phase: 'error', error });
    }
  };

  if (state.phase === 'done') {
    const r = state.result;
    const s = r.summary;
    return (
      <div className="space-y-4">
        <EnvironmentBanner env={env} />
        <WriteOutcome result={r.result} approval={r.approval} warnings={r.warnings} env={env} appliedText={t('pkcs12.applied')} />
        <div className="space-y-2 rounded-lg border p-3">
          <h3 className="text-sm font-semibold">{t('pkcs12.summaryTitle')}</h3>
          <KeyValue
            columns={2}
            rows={[
              [t('certificates.subject'), <span key="s" className="[overflow-wrap:anywhere]">{s.subject}</span>],
              [t('certificates.issuer'), <span key="i" className="[overflow-wrap:anywhere]">{s.issuer}</span>],
              [t('certificates.validity'), `${formatDateTime(s.not_before)} → ${formatDateTime(s.not_after)}`],
              [t('pkcs12.keyType'), s.key_type],
              [t('pkcs12.chainLength'), t('pkcs12.chainCount', { count: s.chain_length })],
              [t('certificates.fingerprint'), <CopyText key="f" value={s.fingerprint_sha256} display={`${s.fingerprint_sha256.slice(0, 16)}…`} />],
              [t('pkcs12.versionState'), t(r.result === 'APPLIED' ? 'pkcs12.stateApplied' : 'pkcs12.statePending', { version: r.item.version })],
            ]}
          />
        </div>
        <FileCaOffer connection={connection} env={env} detail={detail} cas={s.ca_certificates ?? []} onDone={onDone} />
        <DialogFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      <p className="text-sm text-muted-foreground">{active ? t('pkcs12.replaces', { version: active.version }) : t('pkcs12.first')}</p>
      <div className="grid gap-1.5 text-sm">
        <span className="text-xs font-medium text-muted-foreground">{t('pkcs12.file')}</span>
        <div className="flex flex-wrap items-center gap-2">
          <input
            id={fileId}
            type="file"
            accept=".p12,.pfx,application/x-pkcs12"
            className="sr-only"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Button type="button" variant="outline" size="sm" asChild>
            <label htmlFor={fileId} className="cursor-pointer">
              <FileKey2 /> {file ? t('pkcs12.chooseOther') : t('pkcs12.choose')}
            </label>
          </Button>
          {file ? (
            <span className="min-w-0 text-sm break-all">
              {file.name} <span className="text-xs text-muted-foreground">({formatBytes(file.size)})</span>
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">{t('pkcs12.noFile')}</span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{tooLarge ? <span className="text-critical">{t('pkcs12.tooLarge')}</span> : t('pkcs12.fileHint')}</span>
      </div>
      <Field label={t('pkcs12.passphrase')} hint={t('pkcs12.passphraseHint')}>
        <Input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} autoComplete="off" spellCheck={false} />
      </Field>
      <p className="text-xs text-muted-foreground">{env === 'PRODUCTION' ? t('pkcs12.productionNote') : t('pkcs12.testNote')}</p>
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {state.phase === 'error' ? <IntegrationError error={state.error} /> : null}
      {state.phase === 'error' && file ? <p className="text-xs text-muted-foreground">{t('pkcs12.passphraseCleared')}</p> : null}
      <Footer onCancel={onClose} onSubmit={() => void submit()} disabled={!valid} busy={state.phase === 'busy'} env={env} label={env === 'PRODUCTION' ? t('form.submitForApproval') : t('pkcs12.submit')} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revoke (immediate in every environment) and destroy a stored value

export function RevokeDialog({
  open,
  onOpenChange,
  what,
  item,
  connectionId,
  env,
  onDone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  what: 'credential' | 'certificate';
  item: CredentialVersion | CertificateVersion;
  connectionId: string;
  env: IntegrationEnvironment;
  onDone: () => void;
}) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog open={open} onOpenChange={onOpenChange} title={t(`revoke.title.${what}`, { version: item.version })} description={t('revoke.description')}>
      <RevokeForm what={what} item={item} connectionId={connectionId} env={env} onClose={() => onOpenChange(false)} onDone={onDone} />
    </ChangeDialog>
  );
}

function RevokeForm({ what, item, connectionId, env, onClose, onDone }: { what: 'credential' | 'certificate'; item: CredentialVersion | CertificateVersion; connectionId: string; env: IntegrationEnvironment; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const [reason, setReason] = useState('');
  const [confirmActive, setConfirmActive] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite<RevokeBody, { item: CredentialVersion | CertificateVersion }>((body, opts) =>
    what === 'credential' ? api.revokeCredential(connectionId, item.version, body, opts) : api.revokeCertificate(connectionId, item.version, body, opts),
  );
  const isActive = item.status === 'ACTIVE';
  const submit = async () => {
    const res = await write.run({ reason: reason.trim(), confirm_active: isActive && confirmActive });
    if (res) {
      onDone();
      onClose();
    }
  };
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} message={t('revoke.bannerNote')} />
      <Callout tone={isActive ? 'crit' : 'warn'} title={isActive ? t('revoke.activeTitle') : t('revoke.inactiveTitle')}>
        {isActive ? t(`revoke.activeBody.${what}`) : t('revoke.inactiveBody')}
      </Callout>
      {isActive ? <ConfirmCheck checked={confirmActive} onChange={setConfirmActive} text={t('revoke.confirmActive')} /> : null}
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} text={t('revoke.confirm')} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="button" variant="destructive" onClick={() => void submit()} disabled={!reason.trim() || !confirmed || (isActive && !confirmActive) || write.busy}>
          <Ban /> {t('revoke.submit')}
        </Button>
      </DialogFooter>
    </div>
  );
}

export function DestroySecretDialog({ open, onOpenChange, storedSecretId, what, onDone }: { open: boolean; onOpenChange: (v: boolean) => void; storedSecretId: string; what: string; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog open={open} onOpenChange={onOpenChange} title={t('destroy.title')} description={what}>
      <DestroyForm storedSecretId={storedSecretId} onClose={() => onOpenChange(false)} onDone={onDone} />
    </ChangeDialog>
  );
}

function DestroyForm({ storedSecretId, onClose, onDone }: { storedSecretId: string; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: { reason: string }, opts) => api.destroyStoredSecret(storedSecretId, body, opts));
  const submit = async () => {
    const res = await write.run({ reason: reason.trim() });
    if (res) {
      onDone();
      onClose();
    }
  };
  return (
    <div className="space-y-4">
      <Callout tone="warn">{t('destroy.body')}</Callout>
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} text={t('destroy.confirm')} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="button" variant="destructive" onClick={() => void submit()} disabled={!reason.trim() || !confirmed || write.busy}>
          <Trash2 /> {t('destroy.submit')}
        </Button>
      </DialogFooter>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connection record

export function EditConnectionDialog({ open, onOpenChange, connection, onDone }: { open: boolean; onOpenChange: (v: boolean) => void; connection: ConnectionDetail; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog open={open} onOpenChange={onOpenChange} title={t('editConnection.title', { name: connection.name })} description={t('editConnection.description')} wide>
      <EditConnectionForm connection={connection} onClose={() => onOpenChange(false)} onDone={onDone} />
    </ChangeDialog>
  );
}

function EditConnectionForm({ connection: c, onClose, onDone }: { connection: ConnectionDetail; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const [name, setName] = useState(c.name);
  const [status, setStatus] = useState<ConnectionStatus>(c.status);
  const [adapter, setAdapter] = useState<string>(c.adapter);
  const [spec, setSpec] = useState(c.api_spec_reference ?? '');
  const [legalName, setLegalName] = useState(c.upstream?.legal_name ?? '');
  const [tradeName, setTradeName] = useState(c.upstream?.trade_name ?? '');
  const [iana, setIana] = useState(c.upstream ? String(c.upstream.iana_id) : '');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: UpdateConnectionBody, opts) => api.updateConnection(c.id, body, opts));
  const adapters = c.protocol === 'EPP' ? (['standard-epp', 'vnnic-epp'] as const).map((a) => ({ value: a as string, label: t(`adapter.${a}`) })) : [{ value: 'none', label: t('adapter.none') }, ...ctx.rest_adapters.filter((a) => a.kinds.includes(c.kind)).map((a) => ({ value: a.key, label: tx(`adapter.${a.key}`, a.key) }))];
  const ianaNumber = Number(iana);
  const upstreamChanged = c.upstream !== null && (legalName.trim() !== c.upstream.legal_name || tradeName.trim() !== c.upstream.trade_name || ianaNumber !== c.upstream.iana_id);
  const guarded = status !== c.status || adapter !== c.adapter || upstreamChanged;
  const body: UpdateConnectionBody = {
    expected_version: c.version,
    ...(name.trim() !== c.name ? { name: name.trim() } : {}),
    ...(status !== c.status ? { status } : {}),
    ...(adapter !== c.adapter ? { adapter } : {}),
    ...((spec.trim() || null) !== c.api_spec_reference ? { api_spec_reference: spec.trim() || null } : {}),
    ...(upstreamChanged ? { upstream: { legal_name: legalName.trim(), trade_name: tradeName.trim(), iana_id: ianaNumber } } : {}),
    reason: reason.trim(),
  };
  const changed = Object.keys(body).length > 2;
  const upstreamOk = !c.upstream || (legalName.trim() && tradeName.trim() && Number.isInteger(ianaNumber) && ianaNumber > 0);
  const valid = changed && name.trim() && upstreamOk && reason.trim() && confirmed;
  const needsApproval = guarded && c.in_production_use;
  const submit = async () => {
    const res = await write.run(body);
    if (res) onDone();
  };
  if (write.state.phase === 'done') return <Done result={write.state.result} env={needsApproval ? 'PRODUCTION' : null} onClose={onClose} />;
  return (
    <div className="space-y-4">
      {c.in_production_use ? (
        <Callout tone="crit" title={t('editConnection.inProductionTitle')}>
          {t('editConnection.inProduction')}
        </Callout>
      ) : null}
      <div className="grid items-start gap-3 sm:grid-cols-2">
        <Field label={t('create.name')} className="sm:col-span-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
        </Field>
        <SelectField<ConnectionStatus>
          label={t('fields.status')}
          value={status}
          onChange={setStatus}
          options={(['ACTIVE', 'SUSPENDED', 'RETIRED'] as const).map((s) => ({ value: s, label: t(`connectionStatus.${s}`) }))}
          hint={t('editConnection.statusHint')}
        />
        <SelectField label={t('create.adapter')} value={adapter} onChange={setAdapter} options={adapters} />
        <Field label={t('create.spec')} className="sm:col-span-2" hint={t('create.specHint')}>
          <Input value={spec} onChange={(e) => setSpec(e.target.value)} maxLength={300} />
        </Field>
      </div>
      <p className="text-xs text-muted-foreground">{t('editConnection.immutable')}</p>
      {c.upstream ? (
        <fieldset className="grid items-start gap-3 rounded-lg border p-3 sm:grid-cols-2">
          <legend className="px-1 text-sm font-medium">{t('upstream.title')}</legend>
          <p className="text-xs text-muted-foreground sm:col-span-2">{t('upstream.hint')}</p>
          <Field label={t('upstream.legalName')} className="sm:col-span-2">
            <Input value={legalName} onChange={(e) => setLegalName(e.target.value)} maxLength={300} />
          </Field>
          <Field label={t('upstream.tradeName')}>
            <Input value={tradeName} onChange={(e) => setTradeName(e.target.value)} maxLength={300} />
          </Field>
          <Field label={t('upstream.ianaId')}>
            <Input value={iana} onChange={(e) => setIana(e.target.value.replace(/\D/g, ''))} inputMode="numeric" />
          </Field>
        </fieldset>
      ) : null}
      {guarded ? <Callout tone={needsApproval ? 'pending' : 'info'}>{needsApproval ? t('editConnection.willNeedApproval') : t('editConnection.appliesNow')}</Callout> : null}
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={needsApproval ? 'PRODUCTION' : null} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <Footer onCancel={onClose} onSubmit={() => void submit()} disabled={!valid} busy={write.busy} env={needsApproval ? 'PRODUCTION' : null} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registry root CA (server authentication), a separate file from the client certificate

export function RootCaDialog({
  open,
  onOpenChange,
  connectionId,
  connectionName,
  env,
  initialPem,
  onDone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  connectionId: string;
  connectionName: string;
  env: IntegrationEnvironment;
  /** Pre-filled CA (e.g. the CA a failed connection test showed). */
  initialPem?: string;
  onDone: () => void;
}) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog open={open} onOpenChange={onOpenChange} title={t('rootCa.dialogTitle', { name: connectionName })} description={t('rootCa.dialogDescription')} wide>
      {open ? <RootCaForm connectionId={connectionId} env={env} initialPem={initialPem} onClose={() => onOpenChange(false)} onDone={onDone} /> : null}
    </ChangeDialog>
  );
}

function RootCaForm({ connectionId, env, initialPem, onClose, onDone }: { connectionId: string; env: IntegrationEnvironment; initialPem?: string; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const detail = useApi((signal) => api.getConnectionEnvironment(connectionId, { signal }), [connectionId, env]);
  if (detail.status === 'loading') return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (detail.status === 'error') return <IntegrationError error={detail.error} />;
  const base = detail.data.active;
  if (!base?.epp) return <Callout tone="warn">{t('rootCa.needsEnvironment')}</Callout>;
  return <RootCaEditor connectionId={connectionId} env={env} detail={detail.data} epp={base.epp} enabled={base.enabled} initialPem={initialPem} onClose={onClose} onDone={onDone} />;
}

function RootCaEditor({
  connectionId,
  env,
  detail,
  epp,
  enabled,
  initialPem,
  onClose,
  onDone,
}: {
  connectionId: string;
  env: IntegrationEnvironment;
  detail: EnvironmentDetail;
  epp: EppSettings;
  enabled: boolean;
  initialPem?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('integrations');
  const [pem, setPem] = useState(initialPem ?? epp.ca_pem ?? '');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: ProposeEnvironmentBody, opts) => api.proposeEnvironmentVersion(connectionId, body, opts));
  const trimmed = pem.trim();
  const pemOk = trimmed === '' || (trimmed.includes('-----BEGIN CERTIFICATE-----') && trimmed.includes('-----END CERTIFICATE-----'));
  const changed = trimmed !== (epp.ca_pem ?? '').trim();
  const valid = pemOk && changed && reason.trim() && confirmed;
  const submit = async () => {
    const res = await write.run({ expected_generation: detail.generation, enabled, epp: { ...eppForWrite(epp), ca_pem: trimmed || null }, rest: null, reason: reason.trim() });
    if (res) onDone();
  };
  if (write.state.phase === 'done') return <Done result={write.state.result} env={env} onClose={onClose} />;
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      <p className="text-sm text-muted-foreground">{t('rootCa.explain')}</p>
      <Field label={t('rootCa.pem')} hint={!pemOk ? <span className="text-critical">{t('certDialog.pemInvalid')}</span> : trimmed === '' ? t('rootCa.emptyHint') : t('rootCa.pemHint')}>
        <div className="space-y-2">
          <PemFileButton label={t('rootCa.loadFile')} accept=".crt,.cer,.pem,.der" kind="CERTIFICATE" onLoad={(v) => setPem((cur) => (cur.trim() && !initialPem ? `${cur.trim()}\n${v}` : v))} />
          <Textarea value={pem} onChange={(e) => setPem(e.target.value)} rows={8} spellCheck={false} className="font-mono text-xs" maxLength={65536} />
        </div>
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <Footer onCancel={onClose} onSubmit={() => void submit()} disabled={!valid} busy={write.busy} env={env} />
    </div>
  );
}

/** After a PKCS#12 import: the CA certificates of the file, offered as the registry root CA. */
function FileCaOffer({ connection, env, detail, cas, onDone }: { connection: ConnectionDetail; env: IntegrationEnvironment; detail: EnvironmentDetail; cas: PresentedCertificate[]; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const [open, setOpen] = useState(false);
  if (connection.protocol !== 'EPP') return null;
  const configured = new Set((detail.active?.epp?.ca_certificates ?? []).map((c) => c.sha256));
  const fresh = cas.filter((c) => !configured.has(c.sha256));
  if (cas.length === 0) return <Callout tone="info">{t('pkcs12.noCaInFile')}</Callout>;
  if (fresh.length === 0) return <Callout tone="ok">{t('pkcs12.caAlreadyTrusted')}</Callout>;
  const existing = detail.active?.epp?.ca_pem?.trim();
  const merged = [existing, ...fresh.map((c) => c.pem.trim())].filter(Boolean).join('\n');
  return (
    <div className="space-y-2 rounded-lg border border-info/30 bg-info-soft p-3 text-sm">
      <p className="font-medium">{t('pkcs12.caInFile', { count: fresh.length })}</p>
      <ul className="grid gap-1 text-xs">
        {fresh.map((c) => (
          <li key={c.sha256} className="grid gap-0.5">
            <span className="font-medium [overflow-wrap:anywhere]">
              {c.self_signed ? t('rootCa.root') : t('rootCa.intermediate')}: {c.subject}
            </span>
            <span className="text-muted-foreground">
              SHA-256: <CopyText value={c.sha256} display={c.sha256.match(/.{1,2}/g)?.join(':').toUpperCase() ?? c.sha256} />
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">{t('pkcs12.caInFileHint')}</p>
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)}>
        {t('test.serverChain.trust')}
      </Button>
      <RootCaDialog open={open} onOpenChange={setOpen} connectionId={connection.id} connectionName={connection.name} env={env} initialPem={merged} onDone={onDone} />
    </div>
  );
}

